import { useEffect, useState } from 'react';
import { adminApi, type MailTemplateView } from './api';
import { Card, SaveBar, Tag, useSaver } from './ui';

/** E-Mails (Konzept 4.7): die sieben Mails des Anbieters mit Betreff und Text, Platzhalter, Vorschau, Zurücksetzen. */
export function MailsPage() {
  const [mails, setMails] = useState<MailTemplateView[] | null>(null);
  const [placeholders, setPlaceholders] = useState<{ key: string; description: string }[]>([]);

  const load = () =>
    adminApi
      .listMails()
      .then((r) => {
        setMails(r.mails);
        setPlaceholders(r.placeholders);
      })
      .catch(() => setMails([]));
  useEffect(() => void load(), []);

  return (
    <>
      <div className="adm-head">
        <div>
          <h1>E-Mails</h1>
          <p className="muted">Was die Plattform in deinem Namen verschickt. Absender: die Adresse aus den Einstellungen.</p>
        </div>
      </div>
      <details className="adm-card" style={{ marginBottom: 20 }}>
        <summary>
          <strong>Platzhalter</strong> <span className="muted small">– werden beim Versand ausgefüllt; ein Absatz mit leerem Platzhalter fällt weg</span>
        </summary>
        <table className="table" style={{ marginTop: 12 }}>
          <tbody>
            {placeholders.map((p) => (
              <tr key={p.key}>
                <td><code>{`{${p.key}}`}</code></td>
                <td className="muted">{p.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="muted small" style={{ marginTop: 12 }}>Absätze trennst du mit einer Leerzeile. Der erste Absatz, der nur aus einem Link-Platzhalter besteht, wird in der Mail zum Knopf. „» “ am Anfang eines Absatzes macht ein Zitat.</p>
      </details>
      {mails === null && <p className="muted">Wird geladen …</p>}
      {mails?.map((m) => <MailCard key={m.kind} mail={m} onChanged={() => void load()} />)}
    </>
  );
}

function MailCard({ mail, onChanged }: { mail: MailTemplateView; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState(mail.subject);
  const [body, setBody] = useState(mail.body);
  const [preview, setPreview] = useState<{ subject: string; text: string } | null>(null);
  const saver = useSaver();
  const dirty = subject !== mail.subject || body !== mail.body;

  useEffect(() => {
    setSubject(mail.subject);
    setBody(mail.body);
    setPreview(null);
  }, [mail]);

  return (
    <Card
      as="form"
      onSubmit={(e) => {
        e.preventDefault();
        void saver.run(async () => {
          await adminApi.saveMail(mail.kind, { subject, body });
          onChanged();
        });
      }}
      title={
        <button type="button" className="card-toggle" onClick={() => setOpen((v) => !v)}>
          <span className={`card-toggle__chevron${open ? ' card-toggle__chevron--open' : ''}`} aria-hidden="true" />
          {mail.label}
        </button>
      }
      right={<Tag tone={mail.custom ? 'brand' : 'muted'}>{mail.custom ? 'angepasst' : 'Standardtext'}</Tag>}
    >
      {open && (
        <>
          <p className="muted small">Wann: {mail.when}</p>
          <div className="field">
            <label htmlFor={`m-subject-${mail.kind}`}>Betreff</label>
            <input id={`m-subject-${mail.kind}`} type="text" value={subject} maxLength={300} onChange={(e) => setSubject(e.target.value)} required />
          </div>
          <div className="field">
            <label htmlFor={`m-body-${mail.kind}`}>Text</label>
            <textarea id={`m-body-${mail.kind}`} value={body} rows={Math.min(24, Math.max(8, body.split('\n').length + 1))} maxLength={10000} onChange={(e) => setBody(e.target.value)} required />
            {mail.required.length > 0 && <span className="faint small">Muss enthalten: {mail.required.map((r) => `{${r}}`).join(', ')}</span>}
          </div>
          {preview && (
            <div className="mail-preview">
              <span className="kicker">Vorschau mit Beispieldaten</span>
              <p><strong>{preview.subject}</strong></p>
              <pre>{preview.text}</pre>
            </div>
          )}
          <SaveBar saver={saver} dirty={dirty}>
            <button type="button" className="button button--secondary button--small" disabled={saver.busy} onClick={() => void saver.run(async () => setPreview(await adminApi.previewMail(mail.kind, { subject, body })))}>
              Vorschau
            </button>
            {mail.custom && (
              <button type="button" className="link-button" disabled={saver.busy} onClick={() => { if (window.confirm('Diese Mail auf den Standardtext der Plattform zurücksetzen? Deine Änderungen gehen verloren.')) void saver.run(async () => { await adminApi.resetMail(mail.kind); onChanged(); }); }}>
                Auf Standard zurücksetzen
              </button>
            )}
          </SaveBar>
        </>
      )}
    </Card>
  );
}
